import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { Star, MapPin, ChatCircle, Flag, ShieldCheck, Warning } from "@phosphor-icons/react";
import { useSEO } from "../lib/seo";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function WorkerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReview, setShowReview] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  useEffect(() => {
    api.get(`/workers/${id}`)
      .then((r) => setWorker(r.data))
      .catch(() => toast.error("Worker not found"))
      .finally(() => setLoading(false));
  }, [id]);

  useSEO(worker ? {
    title: `${worker.name} — ${worker.headline} in ${worker.city}, ${worker.province}`,
    description: `${worker.name}, ${worker.headline}. ${worker.years_experience} years experience in ${worker.city}. Rated ${worker.rating?.toFixed?.(1) || "—"}/5 by ${worker.review_count || 0} reviewers on CanHireSolution.com.`,
    image: worker.picture,
    path: `/workers/${id}`,
    type: "profile",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Person",
      name: worker.name,
      jobTitle: worker.headline,
      address: { "@type": "PostalAddress", addressLocality: worker.city, addressRegion: worker.province, addressCountry: "CA" },
      aggregateRating: worker.review_count ? {
        "@type": "AggregateRating",
        ratingValue: worker.rating,
        reviewCount: worker.review_count,
      } : undefined,
    },
  } : { title: "Loading worker profile…" });

  const startChat = async () => {
    if (!user) return navigate("/login");
    try {
      const { data } = await api.post("/conversations", { other_user_id: id });
      navigate(`/chat/${data.conversation_id}`);
    } catch (e) {
      toast.error("Could not start chat");
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { worker_id: id, rating, comment });
      toast.success("Review submitted");
      setShowReview(false);
      setComment("");
      const { data } = await api.get(`/workers/${id}`);
      setWorker(data);
    } catch (e) {
      toast.error("Could not submit review");
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!worker) return null;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8">
          <aside className="border border-slate-200 p-6 h-fit">
            <div className="flex items-center gap-3 mb-4">
              {worker.picture ? (
                <img src={worker.picture} alt={worker.name} className="w-16 h-16 object-cover" />
              ) : (
                <div className="w-16 h-16 bg-brand text-white grid place-items-center font-display text-2xl">{worker.name?.[0]}</div>
              )}
              <div>
                <h1 className="font-display text-2xl leading-none flex items-center gap-2">
                  {worker.name}
                  {worker.verification?.status === "verified" && (
                    <ShieldCheck size={20} weight="fill" className="text-emerald-600" data-testid="verified-badge-profile" />
                  )}
                </h1>
                <div className="text-xs text-slate-500 mt-1">{worker.headline}</div>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><MapPin size={16} /> {worker.city}, {worker.province}</div>
              <div className="flex items-center gap-2"><Star size={16} weight="fill" className="text-amber-500" /> {worker.rating?.toFixed?.(1) || "—"} · {worker.review_count} reviews</div>
              <div className="flex items-center gap-2 text-slate-700"><ShieldCheck size={16} className="text-brand" /> {worker.years_experience} years exp</div>
              <div className="text-2xl font-display pt-3">${worker.hourly_rate}<span className="text-sm text-slate-500">/hr CAD</span></div>
            </div>
            <button onClick={startChat} className="w-full bg-brand text-white py-3 mt-6 font-semibold flex items-center justify-center gap-2 hover:bg-[#122148]" data-testid="contact-worker-btn">
              <ChatCircle size={18} weight="bold" /> Message
            </button>
            <button onClick={() => navigate(`/report?target=${id}`)} className="w-full bg-white border border-slate-200 text-slate-700 py-2.5 mt-2 text-sm font-medium flex items-center justify-center gap-2 hover:bg-slate-50" data-testid="report-worker-btn">
              <Flag size={14} /> Report
            </button>
          </aside>

          <main className="md:col-span-2 space-y-8">
            <section>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">About</div>
              <p className="text-base text-slate-700 leading-relaxed whitespace-pre-line">{worker.bio}</p>
            </section>
            {worker.skills?.length > 0 && (
              <section>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Skills</div>
                <div className="flex flex-wrap gap-2">
                  {worker.skills.map((s) => (
                    <span key={s} className="border border-slate-300 px-3 py-1 text-sm">{s}</span>
                  ))}
                </div>
              </section>
            )}
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Reviews ({worker.reviews?.length || 0})</div>
                {user && user.role === "customer" && (
                  <button onClick={() => setShowReview((s) => !s)} className="text-sm font-semibold text-brand" data-testid="leave-review-btn">
                    {showReview ? "Cancel" : "Leave a review"}
                  </button>
                )}
              </div>
              {showReview && (
                <form onSubmit={submitReview} className="border border-slate-200 p-5 mb-5">
                  <div className="flex items-center gap-2 mb-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setRating(n)} data-testid={`star-${n}`}>
                        <Star size={24} weight={n <= rating ? "fill" : "regular"} className={n <= rating ? "text-amber-500" : "text-slate-300"} />
                      </button>
                    ))}
                  </div>
                  <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience…" className="w-full border border-slate-300 p-3 text-sm" rows={3} required data-testid="review-comment" />
                  <button type="submit" className="bg-brand text-white px-5 py-2 text-sm font-semibold mt-3" data-testid="submit-review-btn">Submit review</button>
                </form>
              )}
              <div className="space-y-4">
                {worker.reviews?.length ? worker.reviews.map((r) => (
                  <div key={r.review_id} className="border-b border-slate-200 pb-4">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold text-sm">{r.customer_name}</div>
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map((n) => <Star key={n} size={14} weight={n <= r.rating ? "fill" : "regular"} className={n <= r.rating ? "text-amber-500" : "text-slate-300"} />)}
                      </div>
                    </div>
                    <p className="text-sm text-slate-700">{r.comment}</p>
                    <div className="text-xs text-slate-400 mt-1">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                )) : <p className="text-sm text-slate-500">No reviews yet.</p>}
              </div>
            </section>
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
